import { useMutation, useQuery } from "@apollo/client/react";
import paths from "config/routePaths";
import { GetAllCompanies } from "hooks/apollo/company/company.gql";
import { GetAllRoles } from "hooks/apollo/role/role.gql";
import { GetAssignableSites } from "hooks/apollo/site/site.gql";
import { DeleteUser, GetUser, GetUsers, UpdateUser } from "hooks/apollo/user/user.gql";
import useGoBack from "hooks/useGoBack";
import Authorization from "policies/Authorization";
import React from "react";
import Uuid from "utils/Uuid";
import UserEditView from "./UserEditView";

export default function UserEditContainer({ id, siteId }) {
  const { goBack } = useGoBack();
  const [updateUser] = useMutation(UpdateUser, { onCompleted: () => leavePage() });
  const [deleteUser] = useMutation(DeleteUser, { onCompleted: () => leavePage() });
  const { data: userData, loading: userLoading } = useQuery(GetUser, { variables: { id } });
  const { data: roleData, loading: rolesLoading } = useQuery(GetAllRoles);
  const { data: companiesData, loading: companiesLoading } = useQuery(GetAllCompanies);
  const user = userData?.user;
  const initialRole = user?.role;
  const initialCompanyId = user?.company?.id;
  const {
    previousData: previousSitesData,
    data: sitesData,
    loading: sitesLoading,
    refetch: refetchSites,
  } = useQuery(GetAssignableSites, {
    variables: { companyId: initialCompanyId, role: initialRole },
  });

  const policy = Authorization.usePolicy();
  const canUpdateCompany = policy.canUpdate(Authorization.EntityTypes.Company);

  const loading = userLoading || rolesLoading || companiesLoading || (sitesLoading && !previousSitesData);

  const handleDelete = () => deleteUser({ refetchQueries: [{ query: GetUsers }], variables: { id } });

  const handleCancel = () => leavePage();

  const handleSave = (args) => {
    const defaultUser = {
      email: "",
      id: Uuid.generate(),
      password: "",
      role: "",
    };

    const userToSave = { ...defaultUser, ...userData?.user, ...args, companyId: args.company?.id };

    return updateUser({ refetchQueries: [{ query: GetUsers }], variables: userToSave });
  };

  const leavePage = () => {
    goBack(paths.userIndex);
  };

  if (loading) return null;

  return (
    <UserEditView
      assignableSites={sitesData?.assignableSites || []}
      canUpdateCompany={canUpdateCompany}
      companies={companiesData?.companies || []}
      initialCompanyId={initialCompanyId}
      initialRole={initialRole}
      onCancel={handleCancel}
      onDelete={handleDelete}
      onSave={handleSave}
      refetchSites={refetchSites}
      roles={roleData?.roles || []}
      siteId={siteId}
      user={user}
    />
  );
}
